import { type FormEvent, useEffect, useMemo, useState } from "react";
import {
  createAdminCoupon,
  fetchAdminCoupon,
  fetchAdminCoupons,
  updateAdminCoupon,
  type AdminCouponItem
} from "../../lib/admin-coupons";
import { formatCurrency, formatDateTime } from "../../lib/utils";

type EditorState = {
  id: number | null;
  code: string;
  description: string;
  discount: string;
  maxDiscount: string;
  enabled: boolean;
  type: "PERCENTAGE" | "FIXED";
  expiryDate: string;
};

const emptyEditorState: EditorState = {
  id: null,
  code: "",
  description: "",
  discount: "0",
  maxDiscount: "0",
  enabled: true,
  type: "PERCENTAGE",
  expiryDate: ""
};

function toEditorState(coupon: AdminCouponItem): EditorState {
  return {
    id: coupon.id,
    code: coupon.code,
    description: coupon.description,
    discount: String(coupon.discount),
    maxDiscount: String(coupon.maxDiscount),
    enabled: coupon.enabled,
    type: coupon.type,
    expiryDate: coupon.expiryDateInput
  };
}

function statusTone(status: AdminCouponItem["status"]) {
  if (status === "Active") {
    return "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-900/70 dark:bg-emerald-950/40 dark:text-emerald-300";
  }
  if (status === "Expired") {
    return "border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-900/70 dark:bg-amber-950/40 dark:text-amber-300";
  }
  return "border-slate-300 bg-slate-100 text-slate-700 dark:border-slate-700 dark:bg-slate-900/80 dark:text-slate-300";
}

export function AdminCouponsPage() {
  const [coupons, setCoupons] = useState<AdminCouponItem[]>([]);
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<"all" | "active" | "expired" | "disabled" | "used">("all");
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 25,
    totalCoupons: 0,
    totalPages: 1,
    hasPreviousPage: false,
    hasNextPage: false
  });
  const [summary, setSummary] = useState({
    totalCoupons: 0,
    activeCoupons: 0,
    expiredCoupons: 0,
    usedCoupons: 0
  });
  const [loading, setLoading] = useState(true);
  const [loadingEditor, setLoadingEditor] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editor, setEditor] = useState<EditorState>(emptyEditorState);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const metrics = useMemo(
    () => [
      { label: "Total coupons", value: summary.totalCoupons },
      { label: "Active", value: summary.activeCoupons },
      { label: "Expired", value: summary.expiredCoupons },
      { label: "Used", value: summary.usedCoupons }
    ],
    [summary]
  );

  useEffect(() => {
    let ignore = false;

    async function load() {
      setLoading(true);
      setError(null);

      try {
        const params = new URLSearchParams();
        if (query.trim()) {
          params.set("q", query.trim());
        }
        params.set("status", statusFilter);
        params.set("page", String(page));
        params.set("pageSize", "25");

        const result = await fetchAdminCoupons(params);
        if (!ignore) {
          setCoupons(result.coupons);
          setPagination(result.pagination);
          setSummary(result.summary);
        }
      } catch (loadError) {
        if (!ignore) {
          setError(loadError instanceof Error ? loadError.message : "Unable to load coupons.");
        }
      } finally {
        if (!ignore) {
          setLoading(false);
        }
      }
    }

    void load();

    return () => {
      ignore = true;
    };
  }, [page, query, statusFilter]);

  useEffect(() => {
    setPage(1);
  }, [query, statusFilter]);

  async function reloadCurrentView() {
    const params = new URLSearchParams();
    if (query.trim()) {
      params.set("q", query.trim());
    }
    params.set("status", statusFilter);
    params.set("page", String(page));
    params.set("pageSize", "25");
    const result = await fetchAdminCoupons(params);
    setCoupons(result.coupons);
    setPagination(result.pagination);
    setSummary(result.summary);
  }

  function openCreate() {
    setEditor(emptyEditorState);
    setEditorOpen(true);
    setLoadingEditor(false);
    setError(null);
    setSuccessMessage(null);
  }

  async function openEdit(coupon: AdminCouponItem) {
    setEditorOpen(true);
    setLoadingEditor(true);
    setError(null);
    setSuccessMessage(null);

    try {
      const detail = await fetchAdminCoupon(coupon.id);
      setEditor(toEditorState(detail));
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : "Unable to load coupon details.");
    } finally {
      setLoadingEditor(false);
    }
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();

    if (!editor.code.trim()) {
      setError("Coupon code is required.");
      return;
    }

    const discount = Number(editor.discount);
    const maxDiscount = Number(editor.maxDiscount);

    if (Number.isNaN(discount) || Number.isNaN(maxDiscount)) {
      setError("Discount fields must be valid numbers.");
      return;
    }

    setSaving(true);
    setError(null);
    setSuccessMessage(null);

    try {
      const payload = {
        code: editor.code,
        description: editor.description,
        discount,
        maxDiscount,
        enabled: editor.enabled,
        type: editor.type,
        expiryDate: editor.expiryDate
      };

      let couponId = editor.id;
      if (editor.id) {
        await updateAdminCoupon(editor.id, payload);
      } else {
        const created = await createAdminCoupon(payload);
        couponId = typeof created?.couponId === "number" ? created.couponId : null;
      }

      await reloadCurrentView();
      if (couponId) {
        const refreshed = await fetchAdminCoupon(couponId);
        setEditor(toEditorState(refreshed));
      }
      setSuccessMessage(editor.id ? "Coupon changes saved successfully." : "Coupon created successfully.");
    } catch (submitError) {
      setError(submitError instanceof Error ? submitError.message : "Unable to save coupon.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="space-y-6 pb-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-sm uppercase tracking-[0.2em] text-cyan-300">Coupons</p>
          <h1 className="mt-3 text-4xl font-semibold tracking-tight">Discounts and promotions</h1>
          <p className="mt-4 max-w-3xl text-nv-slate">Create promotions, manage coupon validity, and keep an eye on which codes are actually being used.</p>
        </div>
        <button className="brand-button rounded-full px-5 py-3 text-sm font-semibold text-white" onClick={openCreate} type="button">
          New coupon
        </button>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {metrics.map((metric) => (
          <div className="admin-card rounded-[28px] p-5" key={metric.label}>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-nv-slate">{metric.label}</p>
            <p className="mt-3 text-3xl font-semibold tracking-tight text-nv-ink">{metric.value}</p>
          </div>
        ))}
      </div>

      <div className="grid gap-4 xl:grid-cols-[minmax(0,1.15fr)_minmax(360px,1fr)]">
        <div className="min-w-0 space-y-4">
          <div className="admin-card rounded-[28px] p-5">
            <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_220px]">
              <input className="field-control px-4 py-3 text-sm" onChange={(event) => setQuery(event.target.value)} placeholder="Search by coupon code, type, or description" value={query} />
              <select className="field-control px-4 py-3 text-sm" onChange={(event) => setStatusFilter(event.target.value as typeof statusFilter)} value={statusFilter}>
                <option value="all">All statuses</option>
                <option value="active">Active</option>
                <option value="expired">Expired</option>
                <option value="disabled">Disabled</option>
                <option value="used">Used</option>
              </select>
            </div>
          </div>

          {error ? <div className="rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-700 dark:border-red-900/70 dark:bg-red-950/40 dark:text-red-300">{error}</div> : null}

          <div className="admin-card overflow-hidden rounded-[32px]">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[920px]">
                <thead>
                  <tr className="border-b border-black/10 text-left text-xs font-semibold uppercase tracking-[0.2em] text-nv-slate">
                    <th className="px-5 py-4">Code</th>
                    <th className="px-5 py-4">Type</th>
                    <th className="px-5 py-4">Discount</th>
                    <th className="px-5 py-4">Max</th>
                    <th className="px-5 py-4">Status</th>
                    <th className="px-5 py-4">Usage</th>
                    <th className="px-5 py-4">Expiry</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td className="px-5 py-8 text-sm text-nv-slate" colSpan={7}>
                        Loading coupons...
                      </td>
                    </tr>
                  ) : coupons.length > 0 ? (
                    coupons.map((coupon) => (
                      <tr className="cursor-pointer border-b border-black/5 align-top transition-colors hover:bg-black/[0.03] dark:hover:bg-white/[0.03]" key={coupon.id} onClick={() => void openEdit(coupon)}>
                        <td className="px-5 py-4">
                          <p className="font-semibold text-nv-ink">{coupon.code}</p>
                          <p className="mt-1 text-xs text-nv-slate">{coupon.description || "No description"}</p>
                        </td>
                        <td className="px-5 py-4 text-sm text-nv-slate">{coupon.type}</td>
                        <td className="px-5 py-4 text-sm text-nv-slate">{coupon.type === "PERCENTAGE" ? `${coupon.discount}%` : formatCurrency(coupon.discount)}</td>
                        <td className="px-5 py-4 text-sm text-nv-slate">{formatCurrency(coupon.maxDiscount)}</td>
                        <td className="px-5 py-4">
                          <span className={`inline-flex rounded-full border px-3 py-1 text-xs font-semibold ${statusTone(coupon.status)}`}>{coupon.status}</span>
                        </td>
                        <td className="px-5 py-4 text-sm text-nv-slate">{coupon.usageCount}</td>
                        <td className="px-5 py-4 text-sm text-nv-slate">{coupon.expiryDate ? formatDateTime(coupon.expiryDate) : "No expiry"}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td className="px-5 py-8 text-sm text-nv-slate" colSpan={7}>
                        No coupons matched the current view.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            <div className="flex flex-wrap items-center justify-between gap-3 border-t border-black/5 px-5 py-4 text-sm text-nv-slate dark:border-white/10">
              <p>
                Page {pagination.page} of {pagination.totalPages} · {pagination.totalCoupons} coupons
              </p>
              <div className="flex items-center gap-2">
                <button className="rounded-full border border-black/10 px-4 py-2 font-semibold disabled:opacity-40 dark:border-white/10" disabled={!pagination.hasPreviousPage || loading} onClick={() => setPage((current) => Math.max(1, current - 1))} type="button">
                  Previous
                </button>
                <button className="rounded-full border border-black/10 px-4 py-2 font-semibold disabled:opacity-40 dark:border-white/10" disabled={!pagination.hasNextPage || loading} onClick={() => setPage((current) => current + 1)} type="button">
                  Next
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="min-w-0">
          <div className="admin-card rounded-[32px] p-6">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-sm uppercase tracking-[0.2em] text-cyan-300">{editor.id ? "Edit coupon" : "Create coupon"}</p>
                <h2 className="mt-2 text-2xl font-semibold tracking-tight text-nv-ink">{editor.id ? editor.code : "Promotion details"}</h2>
              </div>
              {editorOpen ? <button className="rounded-full border border-black/10 px-4 py-2 text-sm font-semibold dark:border-white/10" onClick={() => setEditorOpen(false)} type="button">Close</button> : null}
            </div>

            {!editorOpen ? (
              <div className="mt-6 rounded-3xl border border-dashed border-black/10 p-5 text-sm text-nv-slate dark:border-white/10">Select a coupon to edit, or create a new promotion.</div>
            ) : loadingEditor ? (
              <div className="mt-6 rounded-3xl border border-dashed border-black/10 p-5 text-sm text-nv-slate dark:border-white/10">Loading coupon details...</div>
            ) : (
              <form className="mt-6 space-y-5" onSubmit={(event) => void handleSubmit(event)}>
                {successMessage ? <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-700 dark:border-emerald-900/70 dark:bg-emerald-950/40 dark:text-emerald-300">{successMessage}</div> : null}
                <div>
                  <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.2em] text-nv-slate">Coupon code</label>
                  <input className="field-control px-4 py-3 text-sm" onChange={(event) => setEditor((current) => ({ ...current, code: event.target.value.toUpperCase() }))} required value={editor.code} />
                </div>
                <div>
                  <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.2em] text-nv-slate">Description</label>
                  <textarea className="field-control min-h-[100px] px-4 py-3 text-sm" onChange={(event) => setEditor((current) => ({ ...current, description: event.target.value }))} value={editor.description} />
                </div>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.2em] text-nv-slate">Type</label>
                    <select className="field-control px-4 py-3 text-sm" onChange={(event) => setEditor((current) => ({ ...current, type: event.target.value as EditorState["type"] }))} value={editor.type}>
                      <option value="PERCENTAGE">Percentage</option>
                      <option value="FIXED">Fixed amount</option>
                    </select>
                  </div>
                  <div>
                    <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.2em] text-nv-slate">Expiry date</label>
                    <input className="field-control px-4 py-3 text-sm" onChange={(event) => setEditor((current) => ({ ...current, expiryDate: event.target.value }))} type="date" value={editor.expiryDate} />
                  </div>
                  <div>
                    <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.2em] text-nv-slate">Discount</label>
                    <input className="field-control px-4 py-3 text-sm" min="0" onChange={(event) => setEditor((current) => ({ ...current, discount: event.target.value }))} step="0.01" type="number" value={editor.discount} />
                  </div>
                  <div>
                    <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.2em] text-nv-slate">Max discount</label>
                    <input className="field-control px-4 py-3 text-sm" min="0" onChange={(event) => setEditor((current) => ({ ...current, maxDiscount: event.target.value }))} step="0.01" type="number" value={editor.maxDiscount} />
                  </div>
                </div>
                <label className="field-soft flex items-center gap-3 px-4 py-3 text-sm">
                  <input checked={editor.enabled} onChange={(event) => setEditor((current) => ({ ...current, enabled: event.target.checked }))} type="checkbox" />
                  Coupon is enabled
                </label>
                <div className="flex items-center gap-3">
                  <button className="brand-button rounded-full px-5 py-3 text-sm font-semibold text-white disabled:opacity-60" disabled={saving} type="submit">
                    {saving ? "Saving..." : editor.id ? "Save coupon" : "Create coupon"}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
